import { useEffect, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";
import { Upload, Plus, Trash2, Copy, ImageOff, Mail, Bell, Check, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { ROLE_LABELS, WEST_AFRICA_COUNTRIES } from "../lib/fcfa";
import { Switch } from "../components/ui/switch";

export default function Settings() {
  const { user, refreshClub } = useAuth();
  const [club, setClub] = useState(null);
  const [codes, setCodes] = useState([]);
  const [signatures, setSignatures] = useState([]);
  const [newCode, setNewCode] = useState({ code: "", label: "", role: "staff", email: "" });
  const [uploading, setUploading] = useState(false);
  const [resetTarget, setResetTarget] = useState(null);
  const [resetForm, setResetForm] = useState({ new_code: "", confirm: "" });

  const load = async () => {
    const [c, k, s] = await Promise.all([
      api.get("/clubs/me"),
      api.get("/access-codes").catch(() => ({ data: [] })),
      api.get("/signatures").catch(() => ({ data: [] })),
    ]);
    setClub(c.data);
    setCodes(k.data);
    setSignatures(s.data);
  };
  useEffect(() => { load(); }, []);

  const updateClub = async (patch) => {
    const { data } = await api.patch("/clubs/me", patch);
    setClub(data);
    refreshClub();
    toast.success("Paramètres enregistrés");
  };

  const onLogo = async (e, field = "logo_url") => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await updateClub({ [field]: data.url });
    } catch (err) {
      toast.error("Erreur upload");
    } finally {
      setUploading(false);
    }
  };

  const uploadSignature = async (sig, file) => {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
    await api.patch(`/signatures/${sig.id}`, { ...sig, signature_url: data.url });
    toast.success("Signature enregistrée");
    load();
  };

  const addCode = async (e) => {
    e.preventDefault();
    if (newCode.code.length < 4) { toast.error("Code trop court"); return; }
    try {
      await api.post("/access-codes", newCode);
      toast.success("Code créé");
      setNewCode({ code: "", label: "", role: "staff", email: "" });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const deleteCode = async (id) => {
    if (!confirm("Supprimer ce code ?")) return;
    await api.delete(`/access-codes/${id}`);
    load();
  };

  const openReset = (c) => {
    setResetTarget(c);
    setResetForm({ new_code: "", confirm: "" });
  };

  const submitReset = async (e) => {
    e.preventDefault();
    if (resetForm.new_code !== resetForm.confirm) {
      toast.error("Les deux codes ne correspondent pas");
      return;
    }
    if (resetForm.new_code.length < 4) {
      toast.error("Code trop court (4 caractères minimum)");
      return;
    }
    try {
      await api.patch(`/access-codes/${resetTarget.id}/reset-code`, { new_code: resetForm.new_code });
      toast.success(`Code de ${resetTarget.label} mis à jour. Conservez-le précieusement.`);
      setResetTarget(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const copySlug = () => {
    navigator.clipboard.writeText(club.slug);
    toast.success("Identifiant copié");
  };

  if (!club) return <div>Chargement...</div>;

  const logoSrc = (url) => {
    if (!url) return null;
    if (url.startsWith("http")) return url;
    const token = localStorage.getItem("asc_token");
    return `${process.env.REACT_APP_BACKEND_URL}${url}?auth=${token}`;
  };

  return (
    <div>
      <PageHeader overline="Configuration" title="Paramètres du club" description="Identité, équipe administrative et accès." />

      <Tabs defaultValue="identity" className="space-y-4">
        <TabsList className="bg-white border border-[#E0E0E0]">
          <TabsTrigger value="identity" data-testid="tab-identity">Identité</TabsTrigger>
          <TabsTrigger value="documents" data-testid="tab-documents">Documents</TabsTrigger>
          <TabsTrigger value="signatures" data-testid="tab-signatures">Signatures</TabsTrigger>
          <TabsTrigger value="notifications" data-testid="tab-notifications">Notifications</TabsTrigger>
          <TabsTrigger value="codes" data-testid="tab-codes" disabled={user?.role !== "president" && user?.role !== "directeur_general"}>Codes d'accès</TabsTrigger>
        </TabsList>

        <TabsContent value="identity">
          <div className="bg-white border border-[#E0E0E0] rounded-md p-6 space-y-6">
            <div className="flex items-center gap-5">
              <div className="w-20 h-20 rounded-md border border-[#E0E0E0] overflow-hidden bg-[#F4F5F7] flex items-center justify-center">
                {club.logo_url ? (
                  <img src={logoSrc(club.logo_url)} alt="logo" className="w-full h-full object-cover" />
                ) : (
                  <span className="text-2xl font-display font-bold text-[#C62828]">{club.name?.slice(0, 2).toUpperCase()}</span>
                )}
              </div>
              <div>
                <Label>Logo du club</Label>
                <label className="block mt-2 cursor-pointer">
                  <input type="file" accept="image/*" className="hidden" onChange={onLogo} data-testid="settings-logo-input" />
                  <Button type="button" variant="outline" disabled={uploading} asChild>
                    <span><Upload className="w-4 h-4 mr-2" />{uploading ? "Envoi..." : "Téléverser"}</span>
                  </Button>
                </label>
              </div>
            </div>

            <ClubField label="Nom du club" value={club.name} onSave={(v) => updateClub({ name: v })} testid="field-name" />
            <ClubField label="Sous-titre" value={club.subtitle || ""} onSave={(v) => updateClub({ subtitle: v })} testid="field-subtitle" />
            <ClubField label="Ville" value={club.city || ""} onSave={(v) => updateClub({ city: v })} testid="field-city" />

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Pays</Label>
                <Select value={club.country} onValueChange={(v) => updateClub({ country: v })}>
                  <SelectTrigger data-testid="field-country"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {WEST_AFRICA_COUNTRIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Sport principal</Label>
                <Select value={club.sport || "Football"} onValueChange={(v) => updateClub({ sport: v })}>
                  <SelectTrigger data-testid="field-sport"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["Football", "Basketball", "Handball", "Volleyball", "Athlétisme", "Rugby", "Autre"].map((s) => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="border-t border-[#E0E0E0] pt-4">
              <Label>Couleur principale du club</Label>
              <p className="text-xs text-[#616161] mt-1 mb-3">Cette couleur est appliquée à la barre latérale et aux accents de l'interface de votre club.</p>
              <div className="flex flex-wrap items-center gap-2">
                {[
                  { name: "Rouge", v: "#C62828" }, { name: "Vert", v: "#0A6B3A" },
                  { name: "Bleu", v: "#1565C0" }, { name: "Violet", v: "#6A1B9A" },
                  { name: "Orange", v: "#E65100" }, { name: "Noir", v: "#212121" },
                  { name: "Indigo", v: "#283593" }, { name: "Bordeaux", v: "#880E4F" },
                ].map((c) => (
                  <button
                    key={c.v}
                    onClick={() => updateClub({ primary_color: c.v })}
                    title={c.name}
                    data-testid={`color-preset-${c.v}`}
                    className={`w-10 h-10 rounded-md border-2 transition ${club.primary_color === c.v ? "border-[#FCD116] scale-110" : "border-transparent hover:scale-105"}`}
                    style={{ backgroundColor: c.v }}
                  />
                ))}
                <div className="flex items-center gap-2 ml-2 pl-3 border-l border-[#E0E0E0]">
                  <Label className="text-xs mb-0">Personnalisée</Label>
                  <input
                    type="color"
                    value={club.primary_color || "#C62828"}
                    onChange={(e) => updateClub({ primary_color: e.target.value })}
                    data-testid="field-primary-color"
                    className="w-10 h-10 rounded cursor-pointer border border-[#E0E0E0]"
                  />
                </div>
              </div>
              <p className="text-xs text-[#616161] mt-3">
                <span className="inline-block w-3 h-3 rounded mr-2 align-middle" style={{ backgroundColor: club.primary_color || "#C62828" }} />
                Aperçu — actualisez la page pour voir le changement complet dans la barre latérale.
              </p>
            </div>

            <div className="border-t border-[#E0E0E0] pt-4">
              <Label>Identifiant public (slug)</Label>
              <div className="flex gap-2 mt-2">
                <Input value={club.slug} readOnly className="font-mono" />
                <Button type="button" variant="outline" onClick={copySlug} data-testid="copy-slug-btn">
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
              <p className="text-xs text-[#616161] mt-1">Identifiant utilisé pour la connexion de votre équipe.</p>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="documents">
          <div className="bg-white border border-[#E0E0E0] rounded-md p-6 space-y-6">
            <div>
              <h3 className="font-display text-lg font-bold mb-3">Filigrane des documents PDF</h3>
              <div className="flex items-center justify-between border border-[#E0E0E0] rounded-md p-4">
                <div>
                  <p className="font-medium text-sm">Afficher le filigrane</p>
                  <p className="text-xs text-[#616161] mt-1">Logo semi-transparent en arrière-plan des bulletins, attestations et rapports.</p>
                </div>
                <Switch
                  checked={club.show_watermark !== false}
                  onCheckedChange={(v) => updateClub({ show_watermark: v })}
                  data-testid="watermark-toggle"
                />
              </div>
            </div>

            <div>
              <h3 className="font-display text-lg font-bold mb-3">Cachet officiel</h3>
              <div className="flex items-center gap-5">
                <div className="w-24 h-24 rounded-md border border-[#E0E0E0] bg-[#F4F5F7] flex items-center justify-center overflow-hidden">
                  {club.seal_url ? (
                    <img src={logoSrc(club.seal_url)} alt="cachet" className="w-full h-full object-contain p-2" />
                  ) : (
                    <ImageOff className="w-6 h-6 text-[#616161]" />
                  )}
                </div>
                <label className="cursor-pointer">
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => onLogo(e, "seal_url")} data-testid="seal-input" />
                  <Button type="button" variant="outline" disabled={uploading} asChild>
                    <span><Upload className="w-4 h-4 mr-2" /> Téléverser</span>
                  </Button>
                </label>
                {club.seal_url && <Button type="button" variant="ghost" onClick={() => updateClub({ seal_url: null })}>Supprimer</Button>}
              </div>
              <p className="text-xs text-[#616161] mt-2">Le cachet apparaît dans la zone signature des documents officiels.</p>
            </div>

            <div>
              <h3 className="font-display text-lg font-bold mb-3">Filigrane personnalisé</h3>
              <div className="flex items-center gap-5">
                <div className="w-24 h-24 rounded-md border border-[#E0E0E0] bg-[#F4F5F7] flex items-center justify-center overflow-hidden">
                  {club.watermark_url ? (
                    <img src={logoSrc(club.watermark_url)} alt="filigrane" className="w-full h-full object-contain p-2 opacity-40" />
                  ) : (
                    <ImageOff className="w-6 h-6 text-[#616161]" />
                  )}
                </div>
                <label className="cursor-pointer">
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => onLogo(e, "watermark_url")} data-testid="watermark-input" />
                  <Button type="button" variant="outline" disabled={uploading} asChild>
                    <span><Upload className="w-4 h-4 mr-2" /> Téléverser</span>
                  </Button>
                </label>
                {club.watermark_url && <Button type="button" variant="ghost" onClick={() => updateClub({ watermark_url: null })}>Supprimer</Button>}
              </div>
              <p className="text-xs text-[#616161] mt-2">Sinon, le nom du club est utilisé comme filigrane.</p>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="signatures">
          <div className="bg-white border border-[#E0E0E0] rounded-md p-6">
            <h3 className="font-display text-lg font-bold mb-4">Signatures par rôle</h3>
            <p className="text-sm text-[#616161] mb-5">Téléversez les signatures manuscrites scannées qui apparaîtront sur les documents PDF.</p>
            <div className="space-y-3" data-testid="signatures-list">
              {signatures.map((s) => (
                <div key={s.id} className="flex items-center gap-4 border border-[#E0E0E0] rounded-md p-3" data-testid={`signature-${s.role}`}>
                  <div className="w-32 h-16 bg-[#F4F5F7] rounded border border-[#E0E0E0] flex items-center justify-center overflow-hidden">
                    {s.signature_url ? (
                      <img src={logoSrc(s.signature_url)} alt={s.label} className="w-full h-full object-contain" />
                    ) : (
                      <span className="text-xs text-[#616161]">Pas de signature</span>
                    )}
                  </div>
                  <div className="flex-1">
                    <p className="font-medium">{s.label}</p>
                    <p className="text-xs text-[#616161] capitalize">{s.role.replace("_", " ")}</p>
                  </div>
                  <label className="cursor-pointer">
                    <input type="file" accept="image/*" className="hidden"
                      onChange={(e) => e.target.files?.[0] && uploadSignature(s, e.target.files[0])}
                      data-testid={`upload-signature-${s.role}`} />
                    <Button type="button" variant="outline" size="sm" asChild>
                      <span><Upload className="w-3.5 h-3.5 mr-2" /> {s.signature_url ? "Remplacer" : "Téléverser"}</span>
                    </Button>
                  </label>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="notifications">
          <NotificationsTab codes={codes} reload={load} canManage={user?.role === "president" || user?.role === "directeur_general"} />
        </TabsContent>

        <TabsContent value="codes">
          <div className="bg-white border border-[#E0E0E0] rounded-md p-6">
            <h3 className="font-display text-lg font-bold mb-4">Créer un code d'accès</h3>
            <form onSubmit={addCode} className="grid sm:grid-cols-5 gap-3 mb-6">
              <Input placeholder="Code" required minLength={4} value={newCode.code} onChange={(e) => setNewCode({ ...newCode, code: e.target.value })} data-testid="new-code-input" />
              <Input placeholder="Libellé (ex: Jean Coach)" required value={newCode.label} onChange={(e) => setNewCode({ ...newCode, label: e.target.value })} data-testid="new-code-label" />
              <Select value={newCode.role} onValueChange={(v) => setNewCode({ ...newCode, role: v })}>
                <SelectTrigger data-testid="new-code-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(ROLE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
              <Input type="email" placeholder="E-mail (optionnel)" value={newCode.email} onChange={(e) => setNewCode({ ...newCode, email: e.target.value })} data-testid="new-code-email" />
              <Button type="submit" className="btn-club" data-testid="add-code-btn">
                <Plus className="w-4 h-4 mr-2" /> Ajouter
              </Button>
            </form>

            <div className="space-y-2" data-testid="codes-list">
              {codes.map((c) => (
                <div key={c.id} className="flex items-center justify-between border border-[#E0E0E0] rounded-md px-4 py-3">
                  <div className="flex items-center gap-3">
                    <span className="font-mono text-sm bg-[#F4F5F7] px-2 py-1 rounded text-[#616161]" title="Code masqué pour sécurité">••••••••</span>
                    <div>
                      <p className="font-medium text-sm">{c.label}</p>
                      <p className="text-xs text-[#616161]">{ROLE_LABELS[c.role]}{c.email ? ` · ${c.email}` : ""}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className={c.is_active ? "bg-[#2E7D32]/10 text-[#2E7D32] border-[#2E7D32]/30" : ""}>
                      {c.is_active ? "Actif" : "Inactif"}
                    </Badge>
                    <Button size="sm" variant="outline" onClick={() => openReset(c)} data-testid={`reset-code-${c.id}`} title="Modifier le code">
                      <KeyRound className="w-3.5 h-3.5 mr-1" /> Modifier
                    </Button>
                    {c.role !== "president" && (
                      <Button size="sm" variant="ghost" onClick={() => deleteCode(c.id)} data-testid={`delete-code-${c.id}`}>
                        <Trash2 className="w-4 h-4 text-[#C62828]" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <p className="text-xs text-[#616161] mt-4 italic">
              🔒 Pour des raisons de sécurité, les codes sont chiffrés et ne peuvent pas être ré-affichés.
              Utilisez « Modifier » pour définir un nouveau code à tout moment.
            </p>
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={!!resetTarget} onOpenChange={(o) => !o && setResetTarget(null)}>
        <DialogContent className="max-w-md" data-testid="reset-code-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <KeyRound className="w-5 h-5 text-[#C62828]" /> Modifier le code d'accès
            </DialogTitle>
            <DialogDescription>
              Le code actuel sera remplacé. Toutes les sessions ouvertes avec l'ancien code seront fermées immédiatement.
            </DialogDescription>
          </DialogHeader>
          {resetTarget && (
            <form onSubmit={submitReset} className="space-y-3">
              <div className="bg-[#FBC02D]/10 border border-[#FBC02D]/30 rounded p-3 text-sm">
                Membre : <strong>{resetTarget.label}</strong> ({ROLE_LABELS[resetTarget.role]})
              </div>
              <div>
                <Label>Nouveau code *</Label>
                <Input type="text" required minLength={4} value={resetForm.new_code}
                  onChange={(e) => setResetForm({ ...resetForm, new_code: e.target.value })}
                  placeholder="Min. 4 caractères" data-testid="reset-new-code" autoFocus />
              </div>
              <div>
                <Label>Confirmer le nouveau code *</Label>
                <Input type="text" required value={resetForm.confirm}
                  onChange={(e) => setResetForm({ ...resetForm, confirm: e.target.value })}
                  data-testid="reset-confirm-code" />
              </div>
              <div className="bg-[#F4F5F7] border border-[#E0E0E0] rounded p-3 text-xs text-[#616161]">
                💡 Conseil : utilisez au minimum 8 caractères, mélange lettres + chiffres.
                Notez-le immédiatement : <strong>il ne pourra plus être affiché ensuite</strong>.
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setResetTarget(null)}>Annuler</Button>
                <Button type="submit" className="btn-club" data-testid="reset-submit-btn">
                  Enregistrer le nouveau code
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ClubField({ label, value, onSave, testid }) {
  const [v, setV] = useState(value);
  useEffect(() => setV(value), [value]);
  return (
    <div>
      <Label>{label}</Label>
      <div className="flex gap-2 mt-2">
        <Input value={v} onChange={(e) => setV(e.target.value)} data-testid={testid} />
        <Button type="button" variant="outline" onClick={() => onSave(v)} disabled={v === value}>Enregistrer</Button>
      </div>
    </div>
  );
}

function NotificationsTab({ codes, reload, canManage }) {
  return (
    <div className="bg-white border border-[#E0E0E0] rounded-md p-6">
      <div className="flex items-start gap-3 mb-5">
        <div className="w-10 h-10 rounded-md bg-[#0A6B3A]/10 text-[#0A6B3A] flex items-center justify-center shrink-0">
          <Bell className="w-5 h-5" />
        </div>
        <div className="flex-1">
          <h3 className="font-display text-lg font-bold">Notifications e-mail par rôle</h3>
          <p className="text-sm text-[#616161] mt-1">
            Renseignez l'adresse e-mail de chaque membre de votre équipe administrative.
            Ils recevront alors : suspensions du club, rappels de paie mensuelle et contrats expirants.
          </p>
        </div>
      </div>

      {!canManage && (
        <div className="bg-[#FBC02D]/10 border border-[#FBC02D]/30 rounded p-3 text-sm mb-4">
          Seul le Président ou le Directeur Général peut modifier ces e-mails.
        </div>
      )}

      <div className="space-y-2" data-testid="notifications-emails">
        {codes.length === 0 ? (
          <p className="text-sm text-[#616161] py-4">Aucun code d'accès créé. Ajoutez-en dans l'onglet « Codes d'accès ».</p>
        ) : codes.map((c) => (
          <EmailRow key={c.id} code={c} reload={reload} canManage={canManage} />
        ))}
      </div>

      <div className="border-t border-[#E0E0E0] pt-4 mt-6 text-xs text-[#616161]">
        <p className="flex items-center gap-2">
          <Mail className="w-3.5 h-3.5" />
          Expéditeur des e-mails : <strong>WSDK Connect &lt;onboarding@resend.dev&gt;</strong>
        </p>
        <p className="mt-1">Si l'e-mail n'arrive pas, vérifiez le dossier spam.</p>
      </div>
    </div>
  );
}

function EmailRow({ code, reload, canManage }) {
  const [email, setEmail] = useState(code.email || "");
  const [saving, setSaving] = useState(false);
  const isDirty = (email || "") !== (code.email || "");

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/access-codes/${code.id}`, { email: email || "" });
      toast.success(`E-mail ${email ? "enregistré" : "supprimé"} pour ${code.label}`);
      reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex items-center gap-3 border border-[#E0E0E0] rounded-md p-3" data-testid={`email-row-${code.role}`}>
      <div className="w-32 shrink-0">
        <p className="font-medium text-sm">{code.label}</p>
        <p className="text-xs text-[#616161]">{ROLE_LABELS[code.role]}</p>
      </div>
      <div className="flex-1 relative">
        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#616161]" />
        <Input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="exemple@email.com"
          disabled={!canManage}
          className="pl-9 h-9"
          data-testid={`email-input-${code.role}`}
        />
      </div>
      {code.email && !isDirty && (
        <span className="text-[#2E7D32] text-xs flex items-center gap-1">
          <Check className="w-3.5 h-3.5" /> OK
        </span>
      )}
      <Button
        size="sm"
        onClick={save}
        disabled={!isDirty || !canManage || saving}
        className={isDirty ? "btn-club" : ""}
        variant={isDirty ? "default" : "outline"}
        data-testid={`save-email-${code.role}`}
      >
        {saving ? "..." : "Enregistrer"}
      </Button>
    </div>
  );
}
